import { Button } from "../Button/index.js";
import { SearchBar } from "../SearchBar/index.js";
import { LoadingIndicator } from "../LoadingIndicator/index.js";
import { buildLocalItemsFromGrid, getFilterKeyForValue, itemMatchesSearch } from "./TableListColumnFilter.lib.js";
import { AllSelectCheckbox } from "./local-components/AllSelectCheckbox.js";
import { CheckboxList } from "./local-components/CheckboxList.js";
import { GroupedCheckboxList } from "./local-components/GroupedCheckboxList.js";
import React from "react";
import { jsx, jsxs } from "react/jsx-runtime";
import { keepPreviousData, useInfiniteQuery } from "@tanstack/react-query";
import { useGridFilter } from "ag-grid-react";
//#region src/TableListColumnFilter/TableListColumnFilter.tsx
const ALL_SELECT_CHECKBOX_LABEL = "Все элементы";
const TableListColumnFilter = ({ api, model, onModelChange, colDef, getItemLabel, getItemTooltip, getItems, itemsFilter, withAllSelectCheckbox, rowFilter, getNextPageParam, context, getQueryKey, getItemId, expandCellValueToFilterItems, groupsCollapsed = true, withSearch = true }) => {
	const [search, setSearch] = React.useState("");
	const [searchResetKey, setSearchResetKey] = React.useState(0);
	const [selectedItems, setSelectedItems] = React.useState(model?.selectedItemsMap ?? {});
	const [finalSelectedItems, setFinalSelectedItems] = React.useState(model?.selectedItemsMap ?? {});
	const resetFilterSearch = React.useCallback(() => {
		setSearch("");
		setSearchResetKey((key) => key + 1);
	}, []);
	const isFiltersEmpty = !Object.values(selectedItems).some(Boolean);
	const isInitialFiltersEmpty = !Object.values(model?.selectedItemsMap ?? {}).some(Boolean);
	const columnField = colDef.field ?? "";
	const columnId = colDef.colId ?? colDef.field;
	const [gridDataTick, setGridDataTick] = React.useState(0);
	const containerRef = React.useRef(null);
	const [containerMaxHeight, setContainerMaxHeight] = React.useState();
	React.useLayoutEffect(() => {
		const el = containerRef.current;
		if (!el) return;
		const updateHeight = () => {
			const rect = el.getBoundingClientRect();
			const available = window.innerHeight - rect.top - 16;
			if (available > 100) setContainerMaxHeight(available);
		};
		updateHeight();
		requestAnimationFrame(updateHeight);
		const ro = new ResizeObserver(updateHeight);
		ro.observe(document.documentElement);
		return () => ro.disconnect();
	}, []);
	const trimmedSearch = search.trim();
	const searchMatchesAllSelectLabel = Boolean(withAllSelectCheckbox) && trimmedSearch.length > 0 && ALL_SELECT_CHECKBOX_LABEL.toLowerCase().includes(trimmedSearch.toLowerCase());
	const effectiveSearch = searchMatchesAllSelectLabel ? "" : search;
	const { data, isFetchingNextPage, fetchNextPage, isFetching, hasNextPage } = useInfiniteQuery({
		queryKey: getQueryKey ? getQueryKey({
			search: effectiveSearch,
			context,
			page: 1,
			column: columnField
		}) : [
			"table-filter",
			columnField,
			itemsFilter ? null : effectiveSearch
		],
		enabled: Boolean(getItems),
		queryFn: async ({ signal, pageParam }) => getItems ? getItems({
			search: effectiveSearch,
			page: pageParam ?? 1,
			signal,
			context
		}) : [],
		refetchOnMount: true,
		getNextPageParam: getNextPageParam ?? (() => null),
		initialPageParam: 1,
		placeholderData: keepPreviousData
	});
	const remotePagesData = React.useMemo(() => data?.pages.flat() ?? [], [data]);
	const localGridItems = React.useMemo(() => {
		return getItems ? [] : buildLocalItemsFromGrid(api, colDef, getItemId, getItemLabel, expandCellValueToFilterItems);
	}, [
		api,
		colDef,
		expandCellValueToFilterItems,
		getItemId,
		getItemLabel,
		getItems,
		gridDataTick
	]);
	const allPagesData = React.useMemo(() => getItems ? remotePagesData : localGridItems, [
		getItems,
		localGridItems,
		remotePagesData
	]);
	const isServerGrouped = React.useMemo(() => Boolean(getItems) && Array.isArray(allPagesData) && allPagesData.length > 0 && Array.isArray(allPagesData[0]?.items), [allPagesData, getItems]);
	const allItems = React.useMemo(() => {
		if (isServerGrouped) return allPagesData.flatMap((group) => group.items ?? []);
		return allPagesData;
	}, [allPagesData, isServerGrouped]);
	const pagesDataAccumulator = React.useMemo(() => allItems.reduce((acc, item) => {
		const id = getItemId ? getItemId(item) : item.id;
		if (id) acc[id] = item;
		return acc;
	}, {}), [allItems, getItemId]);
	const doesFilterPass = React.useCallback((params) => {
		const rowData = params.data;
		if (!rowData || !colDef.field) return true;
		if (rowFilter) return rowFilter(finalSelectedItems, rowData);
		const cellValue = rowData[colDef.field];
		if (expandCellValueToFilterItems) return expandCellValueToFilterItems(cellValue).some((item) => {
			const id = getItemId ? getItemId(item) : item.id;
			return Boolean(id && finalSelectedItems[id]);
		});
		const key = getFilterKeyForValue(cellValue, getItemId);
		return Boolean(finalSelectedItems[key]);
	}, [
		colDef.field,
		expandCellValueToFilterItems,
		finalSelectedItems,
		getItemId,
		rowFilter
	]);
	useGridFilter({ doesFilterPass });
	const filteredItems = React.useMemo(() => {
		if (!allItems.length || searchMatchesAllSelectLabel) return searchMatchesAllSelectLabel ? [] : allItems;
		if (!effectiveSearch.trim()) return allItems;
		return allItems.filter((item) => itemMatchesSearch(item, effectiveSearch, itemsFilter, getItemLabel));
	}, [
		allItems,
		effectiveSearch,
		getItemLabel,
		itemsFilter,
		searchMatchesAllSelectLabel
	]);
	const sortedItems = React.useMemo(() => {
		const selected = [];
		const unselected = [];
		filteredItems.forEach((item) => {
			const id = getItemId ? getItemId(item) : item.id;
			if (id && selectedItems[id]) selected.push(item);
			else unselected.push(item);
		});
		return [...selected, ...unselected];
	}, [
		filteredItems,
		getItemId,
		selectedItems
	]);
	const groupedItems = React.useMemo(() => {
		if (!isServerGrouped || searchMatchesAllSelectLabel) return [];
		const selectedOutsideGroups = [];
		const groups = allPagesData.map((group) => {
			const items = group.items ?? [];
			const filteredGroupItems = effectiveSearch.trim() ? items.filter((item) => itemMatchesSearch(item, effectiveSearch, itemsFilter, getItemLabel)) : items;
			if (!filteredGroupItems.length) return null;
			const unselected = [];
			filteredGroupItems.forEach((item) => {
				const id = getItemId ? getItemId(item) : item.id;
				if (id && selectedItems[id]) selectedOutsideGroups.push(item);
				else unselected.push(item);
			});
			if (!unselected.length) return null;
			return {
				label: group.label,
				items: unselected
			};
		}).filter((group) => Boolean(group));
		if (!selectedOutsideGroups.length) return groups;
		return [{
			label: "",
			items: selectedOutsideGroups
		}, ...groups];
	}, [
		allPagesData,
		effectiveSearch,
		getItemId,
		getItemLabel,
		isServerGrouped,
		itemsFilter,
		searchMatchesAllSelectLabel,
		selectedItems
	]);
	const isLoading = isFetchingNextPage || isFetching;
	const isSearching = Boolean(trimmedSearch);
	const hasItems = isServerGrouped ? groupedItems.length > 0 : sortedItems.length > 0;
	const isQueryLoading = Boolean(getItems) && isFetching && !isFetchingNextPage;
	const isInitialLoading = isQueryLoading && allItems.length === 0;
	const showLoadingOverlay = isQueryLoading && !(searchMatchesAllSelectLabel && allItems.length > 0);
	const showEmptySearchResult = isSearching && !isLoading && !hasItems && !searchMatchesAllSelectLabel;
	const showAllSelectCheckbox = Boolean(withAllSelectCheckbox) && !showEmptySearchResult && !isInitialLoading && (!isSearching || searchMatchesAllSelectLabel);
	const handleCheckboxChange = (id) => {
		setSelectedItems((prev) => ({
			...prev,
			[id]: !prev[id]
		}));
	};
	const handleApplyClick = () => {
		const nextModel = Object.fromEntries(Object.entries(selectedItems).filter(([, isSelected]) => isSelected));
		onModelChange(Object.keys(nextModel).length > 0 ? {
			selectedItemsMap: nextModel,
			items: Object.values(pagesDataAccumulator).filter((item) => {
				const id = getItemId ? getItemId(item) : item.id;
				return Boolean(id && nextModel[id]);
			})
		} : null);
		setSelectedItems(nextModel);
		setFinalSelectedItems(nextModel);
		resetFilterSearch();
		api.hideColumnFilter();
	};
	const handleResetClick = () => {
		setSelectedItems({});
		setFinalSelectedItems({});
		const columnKey = colDef.colId ?? colDef.field;
		if (columnKey) api.destroyFilter(columnKey);
		resetFilterSearch();
		api.hideColumnFilter();
	};
	React.useEffect(() => {
		setSelectedItems(model?.selectedItemsMap ?? {});
		setFinalSelectedItems(model?.selectedItemsMap ?? {});
	}, [model]);
	React.useEffect(() => {
		const handleFilterOpened = (event) => {
			if ("getColId" in event.column && event.column.getColId() === columnId) resetFilterSearch();
		};
		api.addEventListener("filterOpened", handleFilterOpened);
		return () => {
			api.removeEventListener("filterOpened", handleFilterOpened);
		};
	}, [
		api,
		columnId,
		resetFilterSearch
	]);
	React.useEffect(() => {
		if (getItems) return;
		const onGridDataChange = () => setGridDataTick((value) => value + 1);
		api.addEventListener("rowDataUpdated", onGridDataChange);
		api.addEventListener("modelUpdated", onGridDataChange);
		return () => {
			api.removeEventListener("rowDataUpdated", onGridDataChange);
			api.removeEventListener("modelUpdated", onGridDataChange);
		};
	}, [api, getItems]);
	return /* @__PURE__ */ jsxs("div", {
		ref: containerRef,
		className: "bg-neutrals-white relative flex w-64 flex-col overflow-hidden rounded-xl py-[0.3125rem] shadow",
		style: containerMaxHeight ? { maxHeight: `${containerMaxHeight}px` } : void 0,
		children: [
			withSearch ? /* @__PURE__ */ jsx("div", {
				className: "shrink-0 px-2.5 py-1",
				children: /* @__PURE__ */ jsx(SearchBar, {
					value: search,
					onChange: setSearch,
					size: "sm",
					debounceTime: 350,
					clearable: true
				}, searchResetKey)
			}) : null,
			showAllSelectCheckbox ? /* @__PURE__ */ jsx(AllSelectCheckbox, {
				items: allItems,
				label: ALL_SELECT_CHECKBOX_LABEL,
				getItemId,
				onSelectedItemsChange: setSelectedItems,
				selectedItems
			}) : null,
			/* @__PURE__ */ jsxs("div", {
				className: "relative min-h-0 flex-1 overflow-hidden",
				children: [
					showLoadingOverlay ? /* @__PURE__ */ jsx("div", {
						className: "bg-neutrals-white absolute inset-0 z-10 flex items-center justify-center px-2.5 py-4",
						children: /* @__PURE__ */ jsx(LoadingIndicator, { className: "size-8" })
					}) : null,
					isServerGrouped ? /* @__PURE__ */ jsx(GroupedCheckboxList, {
						groups: groupedItems,
						onChange: handleCheckboxChange,
						selectedItems,
						getItemLabel,
						getItemTooltip,
						hasNextPage,
						onScrollEnd: fetchNextPage,
						isFetching: isFetchingNextPage,
						getItemId,
						groupsCollapsed
					}) : /* @__PURE__ */ jsx(CheckboxList, {
						items: sortedItems,
						onChange: handleCheckboxChange,
						selectedItems,
						getItemLabel,
						getItemTooltip,
						hasNextPage,
						onScrollEnd: fetchNextPage,
						isFetching: isFetchingNextPage,
						getItemId
					}),
					showEmptySearchResult ? /* @__PURE__ */ jsx("div", {
						className: "paragraph-base text-type-common bg-neutrals-white absolute inset-0 flex items-center justify-center px-2.5 py-4 text-center text-sm",
						children: "Ничего не найдено"
					}) : null
				]
			}),
			/* @__PURE__ */ jsxs("div", {
				className: "flex shrink-0 flex-nowrap items-center gap-2 px-2.5 py-1",
				children: [/* @__PURE__ */ jsx(Button, {
					onClick: handleApplyClick,
					theme: "primary",
					size: "sm",
					className: "flex-1",
					type: "button",
					disabled: isInitialFiltersEmpty && isFiltersEmpty,
					children: "Применить"
				}), /* @__PURE__ */ jsx(Button, {
					onClick: handleResetClick,
					size: "sm",
					className: "flex-1",
					type: "button",
					children: "Сбросить"
				})]
			})
		]
	});
};
//#endregion
export { TableListColumnFilter };

//# sourceMappingURL=TableListColumnFilter.js.map